import LandingHeadline from "../common/LandingHeadline";
import {
  FileText,
  ListChecks,
  Code2,
  ClipboardCheck,
  ShieldCheck,
  ScanSearch,
  KanbanSquare,
  Clock,
  ShieldAlert,
  Zap,
  TrendingUp,
  Sparkles,
  CheckCircle2,
  Bug,
  Link2,
  MessageSquareText,
} from "lucide-react";
import Header from "../Header/Header";
import Footer from "../Footer/footer";
import SEOHead from "../SEO/SEOHead";
import { SectionHeading, ScreenshotFrame, FeatureCard, Button, BetaBadge } from "../common/ui";
import "../../css/MainPage.css";
import "../../css/Pages.css";
import "../../css/FrothAI.css";
import askShot from "../../../assets/product/ask-frothai.webp";
import FrothAiIcon from "../common/FrothAiIcon";
import FrothAiIntro from "./FrothAiIntro";
import FrothAiDemo from "./FrothAiDemo";
import FrothAiGuides from "./FrothAiGuides";
import Watermark from "../common/Watermark";

const capabilities = [
  {
    icon: FileText,
    title: "Requirements",
    badge: <BetaBadge />,
    text: "Draft a requirement from a short prompt, then replace or insert the result.",
  },
  {
    icon: ListChecks,
    title: "Test cases",
    badge: <BetaBadge />,
    text: "Generate scenarios and test cases from requirements, scenarios or defects, or auto-fill the test case form.",
  },
  {
    icon: Code2,
    title: "Automation scripts",
    badge: <BetaBadge />,
    text: "Turn approved test cases into Playwright or Appium scripts, run them live and change them by chatting.",
  },
  {
    icon: ClipboardCheck,
    title: "Release readiness",
    text: "Add a FrothAI analysis, risk assessment and ISO 27001 controls mapping to every Test Plan Summary Report.",
  },
  {
    icon: ShieldCheck,
    title: "Security reports",
    text: "Turn Security Test Lab scan results into an AI-written report.",
  },
  {
    icon: ScanSearch,
    title: "Code reports",
    text: "Get AI-written reports for code scans and secret scans from the Code Analyzer.",
  },
  {
    icon: KanbanSquare,
    title: "Tasks",
    text: "Describe the work and let FrothAI break it down into tasks and activities for your board.",
  },
  {
    icon: FrothAiIcon,
    title: "Ask FrothAI",
    badge: <BetaBadge />,
    text: "Ask questions about your project and get answers with evidence and links to the records.",
  },
];

const heroChips = [
  { icon: FileText, label: "Requirements" },
  { icon: ListChecks, label: "Test cases" },
  { icon: Code2, label: "Automation scripts" },
  { icon: ClipboardCheck, label: "Release readiness" },
  { icon: FrothAiIcon, label: "Ask FrothAI" },
];

const benefits = [
  {
    icon: Clock,
    title: "Automate the routine work",
    text: "Spend less time writing test cases and scripts by hand, and more time on high-impact testing strategy.",
  },
  {
    icon: ShieldAlert,
    title: "Reduce human error",
    text: "Improve coverage as FrothAI suggests scenarios across 14 test types you might otherwise miss.",
  },
  {
    icon: Zap,
    title: "Move faster",
    text: "Create test cases and automation scripts quickly to shorten turnaround time and speed up delivery.",
  },
  {
    icon: TrendingUp,
    title: "Focus on results",
    text: "Spend your time on execution, optimisation and delivering high-quality releases.",
  },
];

export default function FrothAI() {
  return (
    <>
      <SEOHead
        title="FrothAI | AI for Test Generation, Automation Scripts & Release Readiness"
        description="FrothAI generates requirements, test cases and Playwright or Appium scripts, answers questions about your project and assesses release readiness, built into Froth TestOps."
        keywords="AI test case generation tool, AI automation script generation, AI software testing tools, FrothAI, Froth TestOps"
      />
      <div className="fd-main-wrapper page-padding">
        <FrothAiIntro />
        <Header />
        <main>
          {/* Fills the first screen: aurora backdrop with a faint FrothAI mark, copy with
              capability chips, and the Ask FrothAI chat with the question and its answer
              floating on its edges. */}
          <section className="ft-page-hero fai-hero">
            <span className="fai-hero__aurora" aria-hidden="true" />
            {/* Arrives as the FrothAiIntro overlay melts away. */}
            <Watermark className="fai-hero__mark" sway delay={2200}>
              <FrothAiIcon size={520} />
            </Watermark>
            <div className="ft-container fai-hero__grid">
              <div className="fai-hero__copy">
                <span className="fai-hero__pill">
                  <span className="fai-hero__pill-mark">
                    <FrothAiIcon size={16} />
                  </span>
                  FrothAI
                  <span className="fai-hero__pill-sep" aria-hidden="true" />
                  <span className="fai-hero__pill-sub">Built into Froth TestOps</span>
                </span>
                <LandingHeadline className="ft-display fai-hero__title">
                  Meet <span>FrothAI</span>
                </LandingHeadline>
                <p className="ft-lead">
                  FrothAI is the AI built into Froth TestOps. It drafts requirements, generates test scenarios and
                  test cases, writes automation scripts, explains your project's data and assesses whether a release
                  is ready, so your team spends less time writing and more time testing.
                </p>
                <div className="ft-btn-row">
                  <Button to="/contact" icon>
                    Talk to Us
                  </Button>
                  <Button to="/workflow" variant="ghost">
                    See the workflow
                  </Button>
                </div>
                <ul className="fai-hero__chips" aria-label="What FrothAI helps with">
                  {heroChips.map(({ icon: Icon, label }) => (
                    <li key={label}>
                      <Icon size={14} aria-hidden="true" />
                      {label}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="fai-hero__visual">
                <span className="fai-hero__orb" aria-hidden="true" />
                <ScreenshotFrame
                  className="fai-hero__shot"
                  src={askShot}
                  priority
                  alt="Ask FrothAI chat answering which open defects are blocking the release"
                />
                <div className="fai-hero__cards" aria-hidden="true">
                  <div className="fai-card fai-card--prompt">
                    <div className="fai-card__head">
                      <FrothAiIcon size={15} />
                      Ask FrothAI
                      <span className="fai-card__typing">
                        <i />
                        <i />
                        <i />
                      </span>
                    </div>
                    <p className="fai-card__prompt">Which open defects are blocking the release?</p>
                  </div>

                  <span className="fai-chip">
                    <Sparkles size={14} />
                    Ask in plain English
                  </span>

                  <div className="fai-card fai-card--output">
                    <div className="fai-card__head">
                      <CheckCircle2 size={15} className="fai-card__ok" />
                      Answered with evidence
                    </div>
                    <ul className="fai-card__list">
                      <li>
                        <Bug size={13} /> 7 open defects
                      </li>
                      <li>
                        <Link2 size={13} /> Links to each record
                      </li>
                      <li>
                        <MessageSquareText size={13} /> Suggested follow-ups
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="ft-section fai-screen fai-caps">
            <div className="ft-container">
              <SectionHeading
                eyebrow="Capabilities"
                title={
                  <>
                    FrothAI across <span>the platform</span>
                  </>
                }
                subtitle="AI that helps at every step, from the first requirement to the release report."
              />
              <div className="ft-grid ft-grid--4">
                {capabilities.map(c => (
                  <FeatureCard key={c.title} {...c} accent />
                ))}
              </div>
            </div>
          </section>

          <FrothAiGuides />

          {/* Live demo: a looping, simulated "Generate from Requirements" run —
              the input types itself in and FrothAI streams out scenarios and
              test cases, right under the how-it-works guides. */}
          <FrothAiDemo />

          {/* Benefits: a dark band to close the page, copy and CTA on the left,
              numbered glass cards on the right. */}
          <section className="ft-section fai-screen fai-bens">
            <span className="fai-bens__aurora" aria-hidden="true" />
            <div className="ft-container fai-bens__grid">
              <div className="fai-bens__intro">
                <span className="ft-eyebrow">Benefits</span>
                <h2 className="ft-heading fai-bens__title">
                  Unlock efficiency with <span>AI-powered testing</span>
                </h2>
                <p className="fai-bens__lead">
                  FrothAI takes on the repetitive work, so your team spends its time where it matters most.
                </p>
                <Button to="/contact" icon>
                  Talk to Us
                </Button>
              </div>
              <ol className="fai-bens__list">
                {benefits.map(({ icon: Icon, title, text }, i) => (
                  <li key={title} className="fai-ben">
                    <span className="fai-ben__num" aria-hidden="true">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="fai-ben__icon" aria-hidden="true">
                      <Icon size={20} />
                    </span>
                    <h3 className="fai-ben__title">{title}</h3>
                    <p className="fai-ben__text">{text}</p>
                  </li>
                ))}
              </ol>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}
